import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FlexColumn } from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import { DbtAssetSourceApi, CodeProjectItem } from 'shared/types';
import ProjectConfigFields from './ProjectConfigFields';

type ProjectForm = {
  repository: string;
  branch: string;
  projectDir: string;
};

type Props = {
  dbtProject: DbtAssetSourceApi;
  existingProject?: CodeProjectItem;
  onFormChange: (
    dbtProjectId: string,
    isValid: boolean,
    isDirty: boolean,
    data: ProjectForm,
  ) => void;
  showProjectName?: boolean;
};

const getDisplayName = (dbtProject: DbtAssetSourceApi): string => {
  if (dbtProject.name) {
    return dbtProject.name;
  }
  if (dbtProject.database) {
    return `${dbtProject.database}.${dbtProject.schema}`;
  }
  return dbtProject.schema;
};

const DbtProjectForm = ({
  dbtProject,
  existingProject,
  onFormChange,
  showProjectName = false,
}: Props) => {
  const form = useForm<ProjectForm>({
    defaultValues: {
      repository: existingProject?.repository || '',
      branch: existingProject?.branch || '',
      projectDir: existingProject?.project_dir || '',
    },
    mode: 'onChange',
  });

  const { watch, formState, reset } = form;

  useEffect(() => {
    if (existingProject) {
      reset({
        repository: existingProject.repository || '',
        branch: existingProject.branch || '',
        projectDir: existingProject.project_dir || '',
      });
    }
  }, [existingProject, dbtProject.id, reset]);

  useEffect(() => {
    const subscription = watch((formData) => {
      onFormChange(dbtProject.id, formState.isValid, formState.isDirty, {
        repository: formData.repository ?? '',
        branch: formData.branch ?? '',
        projectDir: formData.projectDir ?? '',
      });
    });
    return () => subscription.unsubscribe();
  }, [
    watch,
    dbtProject.id,
    onFormChange,
    formState.isValid,
    formState.isDirty,
  ]);

  useEffect(() => {
    const formData = form.getValues();
    onFormChange(dbtProject.id, formState.isValid, formState.isDirty, formData);
  }, [formState.isValid, formState.isDirty, dbtProject.id, onFormChange, form]);

  return (
    <FlexColumn sx={{ gap: 3 }}>
      {showProjectName && (
        <Typography fontWeight={500}>{getDisplayName(dbtProject)}</Typography>
      )}
      <ProjectConfigFields
        register={form.register}
        errors={form.formState.errors}
      />
    </FlexColumn>
  );
};

export default DbtProjectForm;
